import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, RotateCcw, Trash2 } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  cn,
} from "@nexus/ui";
import { PINNED_KPI_NAME, KPI_DECIMALS_MAX, type KpiCatalogEntry, type KpiCatalogFormat, type KpiPolarity, type KpiColorToken } from "@nexus/contracts";
import { kpiCatalogKeys, tenantKpisQueryOptions } from "@/lib/queries";
import { createTenantKpi, deleteTenantKpi, reorderTenantKpis, updateTenantKpi } from "@/features/identity-hub/api/tenant-kpis.api";
import { useIamPermissionForTenant } from "@/features/auth/hooks/usePermissions";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { toast } from "sonner";
import { KpiColorPicker } from "@/features/kpi-settings/components/KpiColorPicker";
import {
  KpiSettingsDialog,
  type KpiSettingsDialogValue,
} from "@/features/kpi-settings/components/KpiSettingsDialog";
import { asKpiColorToken, resolveCatalogColorVar } from "@/features/kpi-settings/lib/kpi-color";
import { clampKpiDecimals } from "@/features/kpi-settings/lib/kpi-decimals";

const FORMAT_OPTIONS: KpiCatalogFormat[] = ["CURRENCY", "PERCENT", "NUMBER"];
const POLARITY_OPTIONS: KpiPolarity[] = ["NEGATIVE", "POSITIVE"];

interface OrgKpiSettingsTabProps {
  tenantId: string;
  locked: boolean;
}

export function OrgKpiSettingsTab({ tenantId, locked }: OrgKpiSettingsTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canManage = useIamPermissionForTenant(tenantId, "IDENTITY_HUB_UPDATE_TENANT");
  const canViewCatalog =
    useIamPermissionForTenant(tenantId, "IDENTITY_HUB_VIEW_PAGE") || canManage;
  const readOnly = locked || !canManage;

  const query = useQuery({
    ...tenantKpisQueryOptions(tenantId),
    enabled: canViewCatalog && !!tenantId,
  });
  const rows = query.data ?? [];
  const [createOpen, setCreateOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortableIds = useMemo(
    () => rows.filter((row) => row.name !== PINNED_KPI_NAME).map((row) => String(row.id)),
    [rows],
  );

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: kpiCatalogKeys.all });
    void queryClient.invalidateQueries({ queryKey: kpiCatalogKeys.tenant(tenantId) });
  }

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof updateTenantKpi>[2] }) =>
      updateTenantKpi(tenantId, id, body),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteTenantKpi(tenantId, id),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const reorderMut = useMutation({
    mutationFn: (names: string[]) => reorderTenantKpis(tenantId, names),
    onSuccess: invalidate,
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const createMut = useMutation({
    mutationFn: (body: Parameters<typeof createTenantKpi>[1]) => createTenantKpi(tenantId, body),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
    },
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  function handleDragEnd(event: DragEndEvent) {
    if (readOnly) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const unpinned = rows.filter((row) => row.name !== PINNED_KPI_NAME);
    const oldIndex = unpinned.findIndex((row) => String(row.id) === String(active.id));
    const newIndex = unpinned.findIndex((row) => String(row.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const nextUnpinned = arrayMove(unpinned, oldIndex, newIndex);
    const pinned = rows.find((row) => row.name === PINNED_KPI_NAME);
    const names = [
      ...(pinned ? [pinned.name] : []),
      ...nextUnpinned.map((row) => row.name),
    ];
    reorderMut.mutate(names);
  }

  if (!canViewCatalog) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("iam.orgSettings.kpis.noPermission", "You do not have permission to view KPIs for this organization.")}
      </p>
    );
  }

  if (query.isLoading) {
    return <div className="h-32 rounded-md border border-border bg-muted/40 animate-pulse" />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            {t("iam.orgSettings.kpis.title", "KPI catalog")}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t(
              "iam.orgSettings.kpis.hint",
              "Hide a KPI to remove it from Overview, Drilldown, and Topic Detail. MCP still sees the full catalog. Spend stays first.",
            )}
          </p>
        </div>
        {canManage && !locked && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            data-testid="org-settings-kpi-create"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            {t("iam.orgSettings.kpis.add", "Add KPI")}
          </Button>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <KpiRow
                key={row.id}
                row={row}
                readOnly={readOnly}
                canDelete={canManage && !locked}
                canReorder={canManage && !locked}
                onPatch={(body) => updateMut.mutate({ id: row.id, body })}
                onReset={() => deleteMut.mutate(row.id)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {/* Same modal the drawers use — `scope="org"` drops the per-user
          "Share with org" switch, since an Org KPI is already shared. */}
      <KpiSettingsDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        scope="org"
        isSubmitting={createMut.isPending}
        onSubmit={(value: KpiSettingsDialogValue) =>
          createMut.mutate({
            displayName: value.displayName,
            formula: value.formula,
            format: value.format,
            polarity: value.polarity,
            decimals: value.decimals,
            visible: true,
            colorToken: value.colorToken,
          })
        }
      />
    </div>
  );
}

function KpiRow({
  row,
  readOnly,
  canDelete,
  canReorder,
  onPatch,
  onReset,
}: {
  row: KpiCatalogEntry;
  readOnly: boolean;
  canDelete: boolean;
  canReorder: boolean;
  onPatch: (body: Parameters<typeof updateTenantKpi>[2]) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const pinned = row.isPinned;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(row.id),
    disabled: pinned || !canReorder,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex flex-col gap-2 rounded-md border border-border bg-background p-2.5",
        isDragging && "z-10 shadow-md",
      )}
      data-testid={`org-settings-kpi-row-${row.name}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground/50",
            pinned || !canReorder ? "cursor-not-allowed" : "cursor-grab hover:text-muted-foreground active:cursor-grabbing",
          )}
          aria-label={t("iam.orgSettings.kpis.reorder", "Reorder {{name}}", { name: row.displayName })}
          disabled={pinned || !canReorder}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={readOnly}
              aria-label={t("kpiSettings.color", "Color")}
              className="size-7 shrink-0 rounded-full border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              style={{ backgroundColor: resolveCatalogColorVar(row.name, row.index, row.colorToken) }}
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto">
            <KpiColorPicker
              value={asKpiColorToken(row.name, row.index, row.colorToken)}
              disabled={readOnly}
              onChange={(colorToken: KpiColorToken) => onPatch({ colorToken })}
            />
          </PopoverContent>
        </Popover>
        <input
          className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:opacity-50"
          defaultValue={row.displayName}
          key={`${row.id}-${row.displayName}`}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.displayName", "Display name")}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next && next !== row.displayName) onPatch({ displayName: next });
          }}
        />
        <div className="flex items-center gap-1.5">
          <Switch
            checked={row.visible}
            disabled={readOnly || pinned}
            aria-label={t("iam.orgSettings.kpis.visible", "Visible")}
            onCheckedChange={(visible) => onPatch({ visible })}
          />
          <span className="text-[11px] text-muted-foreground">
            {row.visible
              ? t("iam.orgSettings.kpis.visibleOn", "Visible")
              : t("iam.orgSettings.kpis.visibleOff", "Hidden")}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-9">
        <select
          className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring disabled:opacity-50"
          value={row.format}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.format", "Format")}
          onChange={(event) => onPatch({ format: event.target.value as KpiCatalogFormat })}
        >
          {FORMAT_OPTIONS.map((format) => (
            <option key={format} value={format}>
              {t(`iam.orgSettings.kpis.format.${format.toLowerCase()}`, format)}
            </option>
          ))}
        </select>
        <select
          className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring disabled:opacity-50"
          value={row.polarity}
          disabled={readOnly}
          aria-label={t("iam.orgSettings.kpis.polarity", "Polarity")}
          onChange={(event) => onPatch({ polarity: event.target.value as KpiPolarity })}
        >
          {POLARITY_OPTIONS.map((polarity) => (
            <option key={polarity} value={polarity}>
              {polarity === "NEGATIVE"
                ? t("iam.orgSettings.kpis.polarityNegative", "Above zero is negative")
                : t("iam.orgSettings.kpis.polarityPositive", "Above zero is positive")}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
          {t("iam.orgSettings.kpis.decimals", "Decimals")}
          <input
            type="number"
            min={0}
            max={KPI_DECIMALS_MAX}
            className="h-8 w-14 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-ring disabled:opacity-50"
            value={row.decimals}
            disabled={readOnly}
            onBlur={(event) => {
              const next = clampKpiDecimals(Number(event.target.value));
              if (next !== row.decimals) onPatch({ decimals: next });
            }}
          />
        </label>
        {row.isOverride && canDelete && (
          <Button type="button" size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={onReset}>
            <RotateCcw className="h-3 w-3" />
            {t("iam.orgSettings.kpis.reset", "Reset to default")}
          </Button>
        )}
        {row.isCustom && canDelete && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 gap-1 text-xs text-destructive"
            onClick={onReset}
          >
            <Trash2 className="h-3 w-3" />
            {t("iam.orgSettings.kpis.delete", "Delete")}
          </Button>
        )}
        {row.isCustom && (
          <span className="text-[11px] text-muted-foreground">{t("iam.orgSettings.kpis.customBadge", "Custom")}</span>
        )}
      </div>
      {row.formula && (
        <p className="truncate pl-9 font-mono text-[11px] text-muted-foreground">{row.formula}</p>
      )}
    </li>
  );
}
